import { CdkDragSortEvent, moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';

import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { DialogConfirmPromptService } from '@libs/src/dialog-confirm-prompt/dialog-confirm-prompt.service';
import { ToastService } from '@libs/src/toast/toast.service';
import { EditHubSegmentationItemsDrawerService } from '@main-client/src/app/edit-hub-segmentation-items-drawer/edit-hub-segmentation-items-drawer.service';
import { HubConfigSegmentationDrawerService } from '@main-client/src/app/hub-configuration/hub-config-segmentations/hub-config-segmentation-drawer/hub-config-segmentation-drawer.service';

import { DeleteMemberCategories } from '@libs/src/accounts/members.actions';
import { getServerError } from '@libs/src/error-handler/error-handler.helper';
import { UpdateGroup } from '@libs/src/groups/groups-store/groups.actions';
import { Group } from '@libs/src/models/group.model';
import { IAppState } from '@main-client/src/app/app.state';

import cloneDeep from 'lodash-es/cloneDeep';
import find from 'lodash-es/find';
import isEmpty from 'lodash-es/isEmpty';

interface Segmentation {
  categories: string[];
  description: string;
  key: string;
  name: string;
}

@Component({
  providers: [DestroySubscriptionsService],
  selector: 'app-hub-config-segmentation',
  styleUrls: ['./hub-config-segmentation.component.scss'],
  templateUrl: './hub-config-segmentation.component.html',
})
export class HubConfigSegmentationComponent implements OnChanges {
  @Input() group: Group;
  @Input() segmentationKey: string;
  @Input() segmentationName: string;
  segmentation: Segmentation;
  goalsBestPractices = {
    body: $localize`:@@hubConfigSegmentation.goals.bestPracticesBody:Create professional development goals that align with your community’s enterprise or academic objectives.<br><br><b>Example:</b> Setting Boundaries in Your Work, Business Development Skills`,
    title: $localize`:@@hubConfigSegmentation.goals.bestPracticesTitle:Create specific and action-oriented goals`,
  };
  interestsBestPractices = {
    body: $localize`:@@hubConfigSegmentation.interests.bestPracticesBody:Create interests that are detailed, and organization-specific. Interests help members get experiences and connections to achieve professional development goals.<br><br><b>Example:</b> Data Fluency, Interpersonal Skills`,
    title: $localize`:@@hubConfigSegmentation.interests.bestPracticesTitle:Craft organization-specific interests to create valuable connections`,
  };

  constructor(
    private readonly editHubSegmentationItemsDrawerService: EditHubSegmentationItemsDrawerService,
    private readonly confirmDialogService: DialogConfirmPromptService,
    private readonly hubConfigSegmentationDrawerService: HubConfigSegmentationDrawerService,
    private readonly store: Store<IAppState>,
    private readonly toastService: ToastService,
  ) {}

  ngOnChanges() {
    this.segmentation = this.initializeSegmentation(
      this.group,
      this.segmentationKey,
      this.segmentationName,
    );
  }

  initializeSegmentation(
    group: Group,
    segmentationKey: string,
    segmentationName: string,
  ) {
    const categoriesSet = new Set((group as any)[segmentationKey]);
    const categories = Array.from(categoriesSet) || [];
    const description =
      find(this.group.field_descriptions, {
        name: segmentationKey,
      })?.description || '';
    return {
      categories,
      description,
      key: segmentationKey,
      name: segmentationName,
    } as Segmentation;
  }

  removeCategory(item: string, segmentation: Segmentation, group: Group) {
    const categoriesSet = new Set(segmentation.categories);
    categoriesSet.delete(item);
    const updatedCategories = Array.from(categoriesSet);
    this.updateCategories(group, segmentation, updatedCategories);
    this.store.dispatch(
      DeleteMemberCategories({
        group,
        categories: [item],
        type: segmentation.name,
      }),
    );
  }

  updateCategories(group: Group, segmentation: Segmentation, update: string[]) {
    this.store.dispatch(
      UpdateGroup({
        group: {
          _id: group._id,
          [segmentation.key]: update,
        },
        originalGroup: group,
      }),
    );
  }

  dropItem(event: CdkDragSortEvent) {
    const categoriesClone = cloneDeep(this.segmentation.categories);
    moveItemInArray(categoriesClone, event.previousIndex, event.currentIndex);
    this.updateCategories(this.group, this.segmentation, categoriesClone);
  }

  openEditHubSegmentationItemsDrawer(group: Group, segmentation: Segmentation) {
    this.editHubSegmentationItemsDrawerService.openDrawer(
      group,
      segmentation.key,
      segmentation.name,
    );
  }

  sortCategoriesAlphabetically(group: Group, segmentation: Segmentation) {
    this.confirmDialogService
      .openDialog({
        cancel: $localize`:@@global.cancel:Cancel`,
        color: 'primary',
        confirm: $localize`:@@global.confirm:Confirm`,
        content: $localize`:@@hubConfigSegmentation.sortDialog.description:Are you sure you want to sort all ${segmentation.name} alphabetically?`,
        title: $localize`:@@hubConfigSegmentation.sortDialog.title:Sort ${segmentation.name} alphabetically`,
      })
      .afterClosed()
      .pipe(
        filter((dialogResponse) => !isEmpty(dialogResponse)),
        tap(() => {
          const orderedCategories = segmentation.categories.sort();
          this.updateCategories(group, segmentation, orderedCategories);
        }),
        catchError((response: HttpErrorResponse) => {
          this.toastService.showError(getServerError(response));
          return EMPTY;
        }),
      )
      .subscribe();
  }

  editDescription(group: Group, segmentationKey: string) {
    this.hubConfigSegmentationDrawerService.openDrawer(group, segmentationKey);
  }
}
